import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, Table, Tag, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BookOpen, FilePlus, ListOrdered, Pencil, Trash2 } from 'lucide-react';
import usePageTitle from '../../../core/hooks/UsePageTitle';
import DefaultLayout from '../../layouts/default/Default.layout';
import MarkdownEditor from '../../components/MarkdownEditor/MarkdownEditor';
import SharedTextEditorModal from '../../components/SharedTextEditor/SharedTextEditorModal';
import SharedTextService from '../../../sdk/services/SharedTextService';
import { SharedText } from '../../../sdk/@types/Acertei';
import { Questao } from '../../../sdk/@types/Questao';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import { readApiError, type ApiError } from '../../../sdk/errors/apiError';

export default function TextosBaseView() {
  usePageTitle('Textos base');

  const [texts, setTexts] = useState<SharedText[]>([]);
  const [loading, setLoading] = useState(true);
  const [denial, setDenial] = useState<ApiError | null>(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SharedText | null>(null);

  const [questionsOf, setQuestionsOf] = useState<SharedText | null>(null);
  const [questions, setQuestions] = useState<Questao.Questao[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const load = () => {
    setLoading(true);
    SharedTextService.list()
      .then((res) => {
        setTexts(res || []);
        setDenial(null);
      })
      // "Erro ao carregar" mentia sobre um 403: não houve erro, houve bloqueio.
      .catch((error) => {
        const apiError = readApiError(error);
        if (apiError.kind) setDenial(apiError);
        else notification.error({ message: 'Erro ao carregar os textos base.' });
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (text: SharedText) => {
    setEditing(text);
    setEditorOpen(true);
  };

  const handleSaved = (saved: SharedText) => {
    setEditorOpen(false);
    setEditing(null);
    setTexts((prev) => {
      const exists = prev.some((t) => t.id === saved.id);
      if (!exists) return [{ ...saved, questionCount: 0 }, ...prev];
      return prev.map((t) => (t.id === saved.id ? { ...saved, questionCount: t.questionCount } : t));
    });
  };

  const showQuestions = async (text: SharedText) => {
    setQuestionsOf(text);
    setLoadingQuestions(true);
    try {
      setQuestions(await SharedTextService.listQuestions(text.id));
    } catch {
      notification.error({ message: 'Erro ao carregar as questões vinculadas.' });
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  /**
   * A API responde 409 quando ainda há questões vinculadas. Nesse caso
   * confirmamos o desvínculo antes de repetir a chamada com `force`.
   */
  const remove = async (text: SharedText, force = false) => {
    try {
      await SharedTextService.remove(text.id, force);
      notification.success({ message: 'Texto base excluído' });
      setTexts((prev) => prev.filter((t) => t.id !== text.id));
    } catch (error) {
      const response = (error as any)?.response;
      if (response?.status === 409) {
        const count = response.data?.question_count ?? 0;
        Modal.confirm({
          title: 'Este texto está em uso',
          content: `${count} questão(ões) usam este texto base. Excluir vai desvinculá-las — as questões continuam existindo, mas sem o texto.`,
          okText: 'Desvincular e excluir',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: () => remove(text, true),
        });
        return;
      }
      notification.error({ message: 'Erro ao excluir o texto base.' });
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return texts;
    return texts.filter((t) =>
      [t.title, t.source, t.body].filter(Boolean).join(' ').toLowerCase().includes(term)
    );
  }, [texts, search]);

  const columns: ColumnsType<SharedText> = [
    {
      title: 'Título',
      dataIndex: 'title',
      render: (title: string, t) => (
        <div className="max-w-md">
          <div className="font-medium text-slate-800 dark:text-slate-100">{title}</div>
          {/* Trecho em texto puro de propósito: a linha da tabela precisa ser
              curta e de altura fixa. O markdown renderizado fica no modal e no
              painel de leitura. */}
          <div className="truncate text-xs text-slate-400 dark:text-[#6b8f6e]">
            {t.body?.replace(/[#*_`>-]/g, ' ').slice(0, 120) || '—'}
          </div>
        </div>
      ),
    },
    {
      title: 'Fonte',
      dataIndex: 'source',
      width: 240,
      render: (source?: string) =>
        source || <span className="text-slate-300 dark:text-[#3a5a3c]">—</span>,
    },
    {
      title: 'Questões',
      dataIndex: 'questionCount',
      width: 120,
      align: 'center',
      render: (count = 0) =>
        count > 0 ? (
          <Tag color="green" bordered={false}>
            {count}
          </Tag>
        ) : (
          <span className="text-slate-300 dark:text-[#3a5a3c]">0</span>
        ),
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      width: 140,
      render: (createdAt?: string) =>
        createdAt ? new Date(createdAt).toLocaleDateString('pt-BR') : '—',
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 150,
      render: (_, t) => (
        <div className="flex items-center gap-1">
          <Button
            type="text"
            size="small"
            title="Ver questões vinculadas"
            icon={<ListOrdered size={16} />}
            onClick={() => showQuestions(t)}
          />
          <Button
            type="text"
            size="small"
            title="Editar"
            icon={<Pencil size={16} />}
            onClick={() => openEdit(t)}
          />
          <Button
            type="text"
            size="small"
            danger
            title="Excluir"
            icon={<Trash2 size={16} />}
            onClick={() => remove(t)}
          />
        </div>
      ),
    },
  ];

  if (denial) {
    return (
      <DefaultLayout hideFilter>
        <div className="p-6">
          <AccessDenied
            kind={denial.kind!}
            detail={denial.raw}
            variant="panel"
            onNavigate={navigate}
          />
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout hideFilter>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
              <BookOpen size={20} />
              Textos base
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-[#6b8f6e]">
              Passagens e diálogos compartilhados por várias questões. No carrossel, as questões de
              um mesmo texto aparecem sempre juntas e em sequência.
            </p>
          </div>
          <Button
            type="primary"
            icon={<FilePlus size={16} />}
            onClick={openCreate}
            style={{ background: '#a3e635', borderColor: '#a3e635', color: '#000', fontWeight: 600 }}
          >
            Novo texto base
          </Button>
        </div>

        <Input.Search
          allowClear
          placeholder="Buscar por título, fonte ou conteúdo"
          className="max-w-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      </div>

      <SharedTextEditorModal
        open={editorOpen}
        text={editing}
        onCancel={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />

      <Modal
        open={Boolean(questionsOf)}
        onCancel={() => setQuestionsOf(null)}
        footer={null}
        width={860}
        title={`Questões de "${questionsOf?.title ?? ''}"`}
      >
        {loadingQuestions ? (
          <p className="text-sm text-gray-500 dark:text-[#6b8f6e]">Carregando...</p>
        ) : questions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-[#6b8f6e]">
            Nenhuma questão vinculada a este texto ainda. Vincule pelo cadastro da questão.
          </p>
        ) : (
          <ol className="flex list-decimal flex-col gap-3 pl-5">
            {questions.map((q) => (
              <li key={String(q.id)} className="text-sm text-slate-700 dark:text-slate-200">
                <span className="mr-2 text-xs text-slate-400 dark:text-[#6b8f6e]">
                  ordem {q.sharedTextOrder ?? '—'}
                </span>
                {/* Enunciado renderizado — antes vinha o markdown cru fatiado. */}
                {q.statement?.trim() ? (
                  <MarkdownEditor readOnly value={q.statement} />
                ) : (
                  <span className="text-slate-400 dark:text-[#6b8f6e]">(sem enunciado)</span>
                )}
              </li>
            ))}
          </ol>
        )}

        {questionsOf && (
          <div className="mt-6 border-t border-gray-200 pt-4 dark:border-[#1a2e1c]">
            <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-[#6b8f6e]">
              Texto
            </p>
            <MarkdownEditor readOnly value={questionsOf.body} />
          </div>
        )}
      </Modal>
    </DefaultLayout>
  );
}
